import { inject, Injectable } from '@angular/core';
import { Location } from '@angular/common';
import { MsalService } from '@azure/msal-angular';
import { defer, Observable, of, tap } from 'rxjs';
import {
  AccountInfo,
  AuthenticationResult,
  EndSessionRequest,
  IdTokenClaims,
  PromptValue,
  RedirectRequest,
  SilentRequest,
  TenantProfile,
} from '@azure/msal-browser';
import { APP_CONFIG } from '@shared/config';
import {
  MSAL_DEFAULT_ENVIRONMENT,
  MSAL_EXP_CLAIM,
  MsalAuthTokenOptions,
  MsalSilentAuthOptions,
} from './models/msal-authentication.model';
import { MSAL_LOGIN_SCOPES } from '@msal/msal.config';
import { catchError, map, shareReplay, switchMap } from 'rxjs/operators';
import { SessionStorageService } from '@shared/services/session-storage.service';
import { LocalStorageService } from '@shared/services/local-storage.service';
import { jwtDecode } from 'jwt-decode';

@Injectable({
  providedIn: 'root',
})
export class MsalAuthenticationService {
  private readonly msalService = inject(MsalService);
  private readonly location = inject(Location);
  private readonly useOAuth = inject(APP_CONFIG)?.useOauth ?? false;
  private redirectResultConsumed = false;
  private redirectResultRequest$: Observable<AuthenticationResult | null> | null =
    null;
  private syntheticAccount: AccountInfo | null = null;

  /** Whether OAuth/MSAL authentication is enabled in the application configuration. */
  public isMsalAuthenticationEnabled(): boolean {
    return !!this.useOAuth;
  }

  /**
   * Set the active MSAL account and persist the login hint to localStorage.
   * Passing `null` clears the active account, the synthetic account, and all MSAL-related storage.
   */
  public setSessionAccount(account: AccountInfo | null) {
    const previousAccount = this.msalService.instance.getActiveAccount();
    this.setSessionLocalStorage(account);

    if (
      previousAccount &&
      account &&
      previousAccount.homeAccountId === account.homeAccountId
    ) {
      return;
    }

    this.msalService.instance.setActiveAccount(account);

    if (!account) {
      this.syntheticAccount = null;
    }
  }

  private setSessionLocalStorage(account: AccountInfo | null) {
    if (!account) {
      LocalStorageService.cleanMsalItems();
      SessionStorageService.clearMsalSilentAttempt();
      SessionStorageService.clearMsalRedirectFailed();
      SessionStorageService.clearMsalRedirectBootAttempts();
      SessionStorageService.clearMsalReturnUrl();
      return;
    }

    const claims = account.idTokenClaims;
    const { login_hint: loginHintClaim } = claims;

    const loginHint = loginHintClaim || account?.username;
    if (loginHint) {
      LocalStorageService.setMsalLoginHint(loginHint);
    }
  }

  /**
   * Re-persist the login hint from the active MSAL account.
   */
  private persistLoginHint(): void {
    const activeAccount = this.msalService.instance.getActiveAccount();
    if (activeAccount) {
      this.setSessionLocalStorage(activeAccount);
    }
  }

  /**
   * Retrieves the current session account from MSAL using a fallback strategy.
   *
   * Resolution order:
   * 1. Returns the active account if one is explicitly set in MSAL.
   * 2. If no active account exists, searches all available accounts and returns
   *    the first one that contains `idTokenClaims` (considered a "preferred" account).
   * 3. If no preferred account is found, returns the first account in the list.
   *
   * If no account was active, set the account that matches the criteria.
   * @returns The resolved account object from MSAL, or `null` if no accounts are available.
   */
  public getSessionAccount(): AccountInfo | null {
    const activeAccount = this.msalService.instance.getActiveAccount();
    if (activeAccount) {
      return activeAccount;
    }

    const accounts = this.msalService.instance.getAllAccounts();
    if (accounts.length > 0) {
      const preferredAccount = accounts.find(
        account => !!account.idTokenClaims
      );
      const account = preferredAccount ? preferredAccount : accounts[0];

      this.setSessionAccount(account);

      return account;
    }

    if (this.syntheticAccount) {
      return this.syntheticAccount;
    }

    return null;
  }

  /**
   * Whether any session (real MSAL or synthetic) is active.
   * Callers that need a real MSAL session with cached tokens must use {@link hasRealMsalAccount}.
   */
  public isSessionActive(): boolean {
    return !!this.getSessionAccount();
  }

  /**
   * Whether MSAL has at least one real account in its cache.
   * Synthetic accounts do not count — they cannot satisfy silent token acquisition.
   */
  public hasRealMsalAccount(): boolean {
    return (
      !!this.msalService.instance.getActiveAccount() ||
      this.msalService.instance.getAllAccounts().length > 0
    );
  }

  /** Get the session expiration as a Unix timestamp in milliseconds. Returns `0` if unknown. */
  public getSessionExpiration(): number {
    const account =
      this.msalService.instance.getActiveAccount() ?? this.syntheticAccount;
    const expSeconds = account?.idTokenClaims?.[MSAL_EXP_CLAIM] as number;
    return expSeconds ? expSeconds * 1000 : 0;
  }

  private hasMsalAccounts(): boolean {
    return this.msalService.instance.getAllAccounts().length > 0;
  }

  /** Promote a cached MSAL account to active if one exists but none is set yet. */
  public restoreSession(): void {
    const activeAccount = this.msalService.instance.getActiveAccount();
    if (activeAccount) {
      return;
    }

    if (this.hasMsalAccounts()) {
      this.msalService.instance.setActiveAccount(this.getSessionAccount());
    }
  }

  /**
   * Ensure the MSAL session is active, attempting silent recovery when possible.
   *
   * Tries to restore a cached account, then falls back to a silent redirect
   * using a persisted login hint. Returns the authentication result on success
   * or `null` when no session could be established without user interaction.
   */
  public ensureSession(): Observable<AuthenticationResult | null> {
    this.restoreSession();
    if (this.isSessionActive()) {
      this.persistLoginHint();
      return of(null);
    }

    if (SessionStorageService.getMsalRedirectFailed()) {
      return of(null);
    }

    const wasSilentAttempt = SessionStorageService.getMsalSilentAttempt();
    const loginHint = LocalStorageService.getMsalLoginHint();

    if (!loginHint && !wasSilentAttempt) {
      return of(null);
    }

    if (wasSilentAttempt) {
      return this.processRedirectReturn();
    }

    return this.authenticateSilently({ loginHint });
  }

  private processRedirectReturn(): Observable<AuthenticationResult | null> {
    return this.resolveRedirectResult().pipe(
      tap(result => {
        SessionStorageService.clearMsalSilentAttempt();
        if (!result) {
          SessionStorageService.setMsalRedirectFailed(true);
        }
      }),
      catchError(err => {
        console.debug('Redirect return processing failed', err);
        SessionStorageService.clearMsalSilentAttempt();
        SessionStorageService.setMsalRedirectFailed(true);
        return of(null);
      })
    );
  }

  /**
   * End the current session. For real MSAL accounts this triggers a redirect to
   * Microsoft's logout endpoint. For synthetic accounts it simply clears local state.
   */
  public logout(): Observable<void> {
    // When the session is synthetic (passkey or QR/device login) there is no
    // real Microsoft session in this browser to end. Calling `logoutRedirect`
    // would send the user to login.microsoftonline.com without an
    // `id_token_hint` (because MSAL has no cached tokens for the synthetic
    // homeAccountId), and Microsoft would not honor `post_logout_redirect_uri`
    // — the user gets stranded on the Microsoft sign-out page.
    if (!this.hasRealMsalAccount()) {
      this.setSessionAccount(null);
      this.clearSyntheticAccount();
      return of(void 0);
    }

    const account = this.getSessionAccount();
    const logoutRequest: EndSessionRequest = {
      ...(account ? { account } : {}),
    };
    this.setSessionAccount(null);
    this.clearSyntheticAccount();

    return this.msalService.logoutRedirect(logoutRequest);
  }

  /** Return the home tenant profile of the current session account, or `null` if unavailable. */
  public getHomeTenant(): TenantProfile | null {
    const account = this.getSessionAccount();
    if (!account) {
      return null;
    }

    if (account.tenantProfiles?.size) {
      const homeProfile = Array.from(account.tenantProfiles.values()).find(
        tenantProfile => tenantProfile.isHomeTenant
      );
      if (homeProfile) return homeProfile;
    }

    if (account.tenantId) {
      return {
        tenantId: account.tenantId,
        localAccountId: account.localAccountId,
        name: account.name,
        isHomeTenant: true,
      } as TenantProfile;
    }

    return null;
  }

  /**
   * Authenticate the user in a web browser using MSAL redirect and silent flows.
   *
   * Resolution order:
   * 1. Processes any pending redirect response.
   * 2. If a redirect response is available, sets the active account and returns it.
   * 3. Otherwise, resolves the current session account and attempts silent token acquisition.
   * 4. If silent acquisition fails and interactive auth is allowed, starts the redirect flow.
   * 5. Returns `null` when no authentication result is available and interaction is not allowed.
   *
   * @param options Authentication options such as scopes, force refresh, interaction allowance, and return URL.
   * @returns An observable emitting the authentication result or `null`.
   */
  public authenticateFromWebBrowser({
    scopes = MSAL_LOGIN_SCOPES,
    forceRefresh = false,
    allowInteraction = false,
    returnUrl,
  }: MsalAuthTokenOptions = {}): Observable<AuthenticationResult | null> {
    const redirectReq: RedirectRequest = {
      scopes,
    };

    return this.resolveRedirectResult().pipe(
      switchMap((redirectResult: AuthenticationResult | null) => {
        if (redirectResult) {
          return of(redirectResult);
        }

        // Synthetic accounts have no tokens in the @azure/msal-browser cache,
        // so acquireTokenSilent against them would always throw. Skip straight
        // to the "no result" branch — callers that need a token (e.g. the
        // token provider) will fall back to the device session id_token.
        const realAccount = this.msalService.instance.getActiveAccount();
        if (!realAccount) {
          if (this.hasRealMsalAccount()) {
            // Active account not set yet but MSAL has cached accounts; promote one.
            this.restoreSession();
          } else if (allowInteraction) {
            return this.startInteractiveAuthentication(redirectReq, returnUrl);
          } else {
            return of(null);
          }
        }

        const accountForSilent = this.msalService.instance.getActiveAccount();
        if (!accountForSilent) {
          return allowInteraction
            ? this.startInteractiveAuthentication(redirectReq, returnUrl)
            : of(null);
        }

        return this.tryAcquireTokenSilent(
          scopes,
          accountForSilent,
          forceRefresh
        ).pipe(
          catchError(error => {
            console.debug(
              'msalAcquireTokenSilent failed authentication',
              error
            );

            if (!allowInteraction) {
              return of(null);
            }

            return this.startInteractiveAuthentication(redirectReq, returnUrl);
          })
        );
      }),
      tap((result: AuthenticationResult | null) =>
        this.setAccountFromAuthenticationResult(result)
      )
    );
  }

  /**
   * Authenticate the MSAL account without user interaction by using a redirect attempt without prompt.
   * The ssoSilent flow cannot be trusted due to third party cookies being blocked by browsers.
   *
   * @param scopes Token scopes
   * @param loginHint Login hint for better account selection.
   */
  public authenticateSilently({
    scopes = MSAL_LOGIN_SCOPES,
    loginHint = null,
  }: MsalSilentAuthOptions = {}): Observable<AuthenticationResult | null> {
    return this.resolveRedirectResult().pipe(
      switchMap((redirectResult: AuthenticationResult | null) => {
        if (redirectResult) {
          SessionStorageService.clearMsalSilentAttempt();
          return of(redirectResult);
        }

        // A silent redirect was started previously but yielded no token.
        // Mark it as failed here so callers that bypass ensureSession()
        // (e.g. trySilentSsoAndGetIdToken) don't start another redirect loop.
        if (SessionStorageService.getMsalSilentAttempt()) {
          SessionStorageService.clearMsalSilentAttempt();
          SessionStorageService.setMsalRedirectFailed(true);
          return of(null);
        }

        if (SessionStorageService.getMsalRedirectFailed()) {
          return of(null);
        }

        const redirectRequest: RedirectRequest = {
          scopes,
          ...(loginHint ? { loginHint } : {}),
          prompt: PromptValue.NONE,
        };

        SessionStorageService.setMsalSilentAttempt(true);

        return this.tryAcquireTokenRedirect(redirectRequest);
      })
    );
  }

  private getSharedRedirectResult(): Observable<AuthenticationResult | null> {
    if (!this.redirectResultRequest$) {
      this.redirectResultRequest$ = defer(() =>
        this.msalService.handleRedirectObservable()
      ).pipe(
        tap((redirectResponse: AuthenticationResult | null) =>
          this.setAccountFromAuthenticationResult(redirectResponse)
        ),
        catchError(err => {
          console.debug('handleRedirectObservable error', err);
          return of(null);
        }),
        shareReplay({ bufferSize: 1, refCount: false })
      );
    }

    return this.redirectResultRequest$;
  }

  private resolveRedirectResult(): Observable<AuthenticationResult | null> {
    return this.getSharedRedirectResult().pipe(
      map((redirectResponse: AuthenticationResult | null) => {
        if (!redirectResponse || this.redirectResultConsumed) {
          return null;
        }

        this.redirectResultConsumed = true;

        return redirectResponse;
      })
    );
  }

  private tryAcquireTokenSilent(
    scopes: string[],
    account: AccountInfo,
    forceRefresh: boolean
  ): Observable<AuthenticationResult> {
    const silentRequest: SilentRequest = {
      scopes,
      account,
      forceRefresh,
    };

    return this.msalService.acquireTokenSilent(silentRequest);
  }

  private startInteractiveAuthentication(
    request: RedirectRequest,
    returnUrl?: string
  ): Observable<AuthenticationResult | null> {
    return this.tryAcquireTokenRedirect(request, returnUrl).pipe(
      catchError(error => {
        console.debug('msalAcquireTokenRedirect failed', error);
        return this.tryLoginRedirect(request, returnUrl);
      })
    );
  }

  private tryAcquireTokenRedirect(
    request: RedirectRequest,
    returnUrl?: string
  ): Observable<AuthenticationResult | null> {
    SessionStorageService.clearMsalRedirectBootAttempts();
    SessionStorageService.setMsalReturnUrl(
      returnUrl ?? this.location.path(true)
    );
    return this.msalService
      .acquireTokenRedirect(this.withBaseRedirectStartPage(request))
      .pipe(map(() => null));
  }

  private tryLoginRedirect(
    request: RedirectRequest,
    returnUrl?: string
  ): Observable<AuthenticationResult | null> {
    SessionStorageService.clearMsalRedirectBootAttempts();
    SessionStorageService.setMsalReturnUrl(
      returnUrl ?? this.location.path(true)
    );
    return this.msalService
      .loginRedirect(this.withBaseRedirectStartPage(request))
      .pipe(map(() => null));
  }

  /**
   * Force the redirect flow to start (and therefore return) at the app base
   * URL.
   */
  private withBaseRedirectStartPage(request: RedirectRequest): RedirectRequest {
    return { ...request, redirectStartPage: document.baseURI };
  }

  private setAccountFromAuthenticationResult(
    result: AuthenticationResult | null
  ) {
    if (!result?.account) {
      return;
    }

    this.setSessionAccount(result.account);
  }

  /** Handle an MFA/claims challenge by redirecting to acquire a token with the required claims. */
  public handleMfaChallenge(
    claims: string,
    tenantId: string,
    scopes: string[] | string
  ) {
    const normalizedScopes: string[] = Array.isArray(scopes)
      ? scopes
      : scopes.split(' ');
    const request: RedirectRequest = {
      scopes: normalizedScopes,
      claims,
      authority: `https://login.microsoftonline.com/${tenantId}`,
      account: this.msalService.instance.getActiveAccount(),
    };

    return this.tryAcquireTokenRedirect(request);
  }

  /**
   * Build an in-memory synthetic {@link AccountInfo} from a backend-provided id_token.
   *
   * Used after passkey or QR/device-code login: there is no real MSAL session,
   * but the backend mints a Microsoft id_token that lets the app identify the
   * signed-in user. The account lives only in memory (not persisted) and is
   * re-hydrated via `/auth/device/token` on page refresh.
   */
  public hydrateSyntheticAccountFromIdToken(idToken: string): void {
    let claims: Record<string, unknown>;
    try {
      claims = jwtDecode<Record<string, unknown>>(idToken);
    } catch {
      return;
    }

    const tenantId = claims['tid'] as string | undefined;
    const username =
      (claims['preferred_username'] as string | undefined) ||
      (claims['upn'] as string | undefined) ||
      (claims['email'] as string | undefined) ||
      (claims['unique_name'] as string | undefined);
    const localAccountId =
      (claims['oid'] as string | undefined) ||
      (claims['sub'] as string | undefined);

    if (!tenantId || !username || !localAccountId) {
      return;
    }

    const environment = this.extractEnvironmentFromIssuer(
      claims['iss'] as string | undefined
    );

    this.syntheticAccount = {
      homeAccountId: `${localAccountId}.${tenantId}`,
      environment,
      localAccountId,
      tenantId,
      username,
      name: claims['name'] as string | undefined,
      idTokenClaims: claims as IdTokenClaims,
      tenantProfiles: new Map<string, TenantProfile>(),
    };
  }

  /** Remove the in-memory synthetic account created by {@link hydrateSyntheticAccountFromIdToken}. */
  public clearSyntheticAccount(): void {
    this.syntheticAccount = null;
  }

  /** Whether an in-memory synthetic account is currently hydrated. */
  public hasSyntheticAccount(): boolean {
    return !!this.syntheticAccount;
  }

  private extractEnvironmentFromIssuer(iss: string | undefined): string {
    if (!iss) return MSAL_DEFAULT_ENVIRONMENT;
    try {
      return new URL(iss).hostname;
    } catch {
      return MSAL_DEFAULT_ENVIRONMENT;
    }
  }
}
